import React from "react";
import PropTypes from "prop-types";
import ConfidenceBadge from "./ConfidenceBadge";

// Attention treatment per status. Only present when something actually needs action — a healthy
// dashboard shows none of this (calm by default; the mere presence of the accent means "look here").
// Amber = at risk, red = over / breaching.
const ATTENTION = {
  // Most off-plan states read as a calm amber "Needs attention"...
  at_risk: { label: "Needs attention", dot: "bg-warning", text: "text-warning", cssVar: "warning" },
  over: { label: "Needs attention", dot: "bg-warning", text: "text-warning", cssVar: "warning" },
  // ...red "Action needed" is reserved for the most-severe tier (e.g. an imminent breach).
  red: { label: "Action needed", dot: "bg-danger", text: "text-danger", cssVar: "danger" },
};

// The action label/target depends on what's driving the verdict (handled by onReview in the page).
const REVIEW_LABEL = {
  budget: "Review budget",
  spend: "Review total spend",
  unassigned: "Review unassigned spend",
  confidence: "Review forecast",
};

// ExecutiveSummary is also the verdict: it shows the LLM narrative (or deterministic bullets, or the
// all-clear line) and — only when a signal warrants it — a calm attention cue (recoloured label +
// thin left edge) and a quiet inline action. The heavy standalone banner is gone.
const ExecutiveSummary = ({ prose = "", bullets = [], overall = {}, onReview, confidence }) => {
  const status = overall.status || "green";
  const attn = ATTENTION[status]; // undefined when in control
  const reviewLabel = REVIEW_LABEL[overall.driver] || "Review";

  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        {attn ? (
          // The label slot itself becomes the signal: a coloured word where a grey label usually sits.
          <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${attn.text}`}>
            <span className={`inline-block rounded-full ${attn.dot}`} style={{ width: 8, height: 8 }} />
            {attn.label}
          </div>
        ) : (
          <div className="text-xs uppercase tracking-wide text-text-2">Executive summary</div>
        )}
        <ConfidenceBadge confidence={confidence} />
      </div>
      {/* The verdict one-liner leads (bold); the LLM prose / bullets follow as supporting detail. */}
      {overall.headline ? (
        <p className="text-sm font-semibold leading-snug text-text">{overall.headline}</p>
      ) : null}
      {prose ? (
        <p className={`text-sm leading-relaxed text-text-2 ${overall.headline ? "mt-1.5" : ""}`}>{prose}</p>
      ) : bullets.length > 0 ? (
        <ul className={`space-y-1.5 ${overall.headline ? "mt-1.5" : ""}`}>
          {bullets.map((b, idx) => (
            <li key={`${idx}-${b.slice(0, 40)}`} className="text-sm leading-relaxed text-text-2">
              {b}
            </li>
          ))}
        </ul>
      ) : !overall.headline ? (
        <p className="text-sm text-text-2">All clear — nothing needs your attention right now.</p>
      ) : null}
      {attn && onReview ? (
        <button
          type="button"
          onClick={() => onReview(overall)}
          className="mt-2.5 text-sm font-semibold text-primary hover:text-primary-hover"
        >
          {reviewLabel} →
        </button>
      ) : null}
    </div>
  );
};

ExecutiveSummary.propTypes = {
  prose: PropTypes.string,
  bullets: PropTypes.arrayOf(PropTypes.string),
  overall: PropTypes.shape({ status: PropTypes.string, headline: PropTypes.string, driver: PropTypes.string, primary_budget_id: PropTypes.number }),
  onReview: PropTypes.func,
  confidence: PropTypes.shape({ level: PropTypes.string, mape: PropTypes.number, basis: PropTypes.string }),
};

export default ExecutiveSummary;
